import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, PlusCircle, History, Target, Share2, Eye, Trash2, Flag } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const SOURCE_LABEL = {
  live_rounds: { label: 'Live Round', color: 'bg-emerald-100 text-emerald-700' },
  social_posts: { label: 'Posted', color: 'bg-blue-100 text-blue-700' },
  leaderboard_entries: { label: 'Tagged', color: 'bg-purple-100 text-purple-700' },
  golf_rounds: { label: 'Logged', color: 'bg-gray-100 text-gray-700' },
};

const formatVsPar = (v) => v === 0 ? 'E' : v > 0 ? `+${v}` : `${v}`;

export default function RoundHistory() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(true);
  const [shareTarget, setShareTarget] = useState(null);
  const shareCardRef = useRef(null);

  useEffect(() => { if (!loading && !user) navigate('/'); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchAll = async () => {
      try {
        const res = await fetch(`${API}/me/rounds`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setRounds(data.rounds || []);
        }
      } catch (e) { console.error(e); }
      setLoadingRounds(false);
    };
    fetchAll();
  }, [user]);

  const handleShare = async (round) => {
    const fallbackText = `⛳ ${round.course_name} — Score ${round.score} (${formatVsPar(round.score_vs_par)}) on ${round.date}${user?.name ? `\n— ${user.name}` : ''}`;
    // If we have hole-by-hole data, render the scorecard image
    if (round.scorecard_data?.players?.length) {
      setShareTarget(round);
      // Wait a tick so React mounts the hidden share card
      await new Promise(r => setTimeout(r, 60));
      try {
        const node = shareCardRef.current;
        if (node) {
          const canvas = await html2canvas(node, { backgroundColor: '#f5f1e8', scale: 2, useCORS: true });
          await new Promise((resolve) => canvas.toBlob(async (blob) => {
            if (!blob) { resolve(); return; }
            const file = new File([blob], `bundle-scorecard-${round.date}.png`, { type: 'image/png' });
            if (navigator.canShare?.({ files: [file] })) {
              try { await navigator.share({ files: [file], title: `Round at ${round.course_name}`, text: fallbackText }); resolve(); return; } catch (e) { console.warn('share file failed', e); }
            }
            // Fallback: download the image
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `bundle-scorecard-${round.date}.png`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success('Scorecard saved to downloads');
            resolve();
          }, 'image/png'));
        }
      } catch (e) {
        console.error('share image failed', e);
      } finally {
        setShareTarget(null);
      }
      return;
    }
    // No hole-by-hole data → text share
    if (navigator.share) {
      try { await navigator.share({ title: `Round at ${round.course_name}`, text: fallbackText }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(fallbackText);
      toast.success('Round details copied to clipboard');
    } catch {
      toast.error('Could not copy. Try again.');
    }
  };

  const handleView = (round) => {
    if (round.live_round_id) navigate(`/live-round/${round.live_round_id}`);
    else if (round.post_id) navigate(`/feed?post=${round.post_id}`);
    else toast.info('No detailed view available for this round');
  };

  const handleDelete = async (round) => {
    if (!window.confirm(`Delete this round at ${round.course_name}?`)) return;
    try {
      let res;
      if (round.source === 'live_rounds' || round.source === 'social_posts' || round.source === 'leaderboard_entries') {
        // Use the admin endpoint if admin, else self-delete via leaderboard endpoint
        if (user?.is_admin) {
          res = await fetch(`${API}/admin/round/${round.source}/${encodeURIComponent(round.round_id)}`, { method: 'DELETE', credentials: 'include' });
        } else {
          // Members delete by course
          res = await fetch(`${API}/leaderboard/my-rounds/${encodeURIComponent(round.course_name)}`, { method: 'DELETE', credentials: 'include' });
        }
      } else if (round.source === 'golf_rounds') {
        res = await fetch(`${API}/rounds/${round.round_id}`, { method: 'DELETE', credentials: 'include' });
      }
      if (res?.ok) {
        toast.success('Round deleted');
        setRounds(rounds.filter(r => r.round_id !== round.round_id));
      } else {
        toast.error('Failed to delete');
      }
    } catch { toast.error('Network error'); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-4xl mx-auto px-4 md:px-12 pt-20 md:pt-24">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2" data-testid="round-history-back">
          <ArrowLeft className="w-5 h-5 mr-2" /> Back
        </Button>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-3xl text-bundle-green mb-1" data-testid="round-history-title">My Rounds</h1>
            <p className="font-body text-gray-600 text-sm">{rounds.length} round{rounds.length === 1 ? '' : 's'} across all sources</p>
          </div>
          <Button data-testid="post-new-round-btn" onClick={() => navigate('/post-round')} className="btn-bundle">
            <PlusCircle className="w-4 h-4 mr-2" /> Post New Round
          </Button>
        </div>

        {loadingRounds ? (
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
            </CardContent>
          </Card>
        ) : rounds.length === 0 ? (
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="p-12 text-center">
              <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-bundle-green mb-2">No rounds yet</h3>
              <p className="font-body text-gray-500 mb-6">Start tracking your golf rounds</p>
              <Button onClick={() => navigate('/post-round')} className="btn-bundle">
                <PlusCircle className="w-4 h-4 mr-2" /> Post Your First Round
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {rounds.map((r, idx) => {
              const sourceMeta = SOURCE_LABEL[r.source] || { label: r.source, color: 'bg-gray-100 text-gray-700' };
              return (
                <Card key={`${r.source}_${r.round_id}_${idx}`} className="bg-white border-bundle-cream-dark hover:shadow-md transition-shadow" data-testid={`round-card-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${sourceMeta.color}`}>{sourceMeta.label}</span>
                          {r.tagged && <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">You were tagged</span>}
                          <span className="text-xs text-gray-500">{r.date}</span>
                        </div>
                        <p className="font-heading font-bold text-bundle-green text-base truncate">{r.course_name}</p>
                        {r.tee_box && <p className="text-xs text-gray-500">{r.tee_box} tees · {r.holes_played} holes</p>}
                        {r.caption && <p className="text-xs text-gray-600 mt-1 line-clamp-1 italic">"{r.caption}"</p>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-center">
                          <p className="font-heading text-2xl font-bold text-bundle-green leading-none">{r.score}</p>
                          <p className={`text-xs font-semibold ${r.score_vs_par < 0 ? 'text-red-600' : r.score_vs_par === 0 ? 'text-bundle-green' : 'text-gray-500'}`}>
                            {formatVsPar(r.score_vs_par)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-bundle-cream-dark">
                      <Button size="sm" variant="outline" onClick={() => handleShare(r)} data-testid={`share-round-${idx}`} className="flex-1 border-bundle-green/30 text-bundle-green hover:bg-bundle-green/5">
                        <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
                      </Button>
                      {(r.live_round_id || r.post_id) && (
                        <Button size="sm" variant="outline" onClick={() => handleView(r)} data-testid={`view-round-${idx}`} className="flex-1 border-bundle-green/30 text-bundle-green hover:bg-bundle-green/5">
                          <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} data-testid={`delete-round-${idx}`} className="text-gray-400 hover:text-red-500 hover:bg-red-50 px-2">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Off-screen share card — rendered only while generating share image */}
      {shareTarget && (
        <div style={{ position: 'fixed', left: -10000, top: 0, width: 720, background: '#f5f1e8' }}>
          <ShareScorecardCard ref={shareCardRef} round={shareTarget} user={user} />
        </div>
      )}
    </div>
  );
}

const ShareScorecardCard = React.forwardRef(({ round, user }, ref) => {
  const sc = round.scorecard_data || {};
  const players = sc.players || [];
  const pars = sc.pars || (players.find(p => p.pars)?.pars) || {};
  const numHoles = sc.num_holes || 18;
  const holes = Array.from({ length: numHoles }, (_, i) => i + 1);
  const front9 = holes.slice(0, 9);
  const back9 = holes.slice(9, 18);

  const getScore = (player, h) => {
    if (player.scores) {
      const s = player.scores[String(h)] ?? player.scores[h];
      return (s != null && s !== '') ? s : null;
    }
    if (player.holes) {
      const cell = player.holes[h - 1];
      return cell?.score ?? null;
    }
    return null;
  };
  const sumRange = (player, range) => range.reduce((acc, h) => { const s = getScore(player, h); return acc + (typeof s === 'number' ? s : 0); }, 0);
  const sumPar = (range) => range.reduce((acc, h) => acc + (Number(pars[String(h)] ?? pars[h]) || 0), 0);

  const renderSection = (range, label) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginBottom: 12 }}>
      <thead>
        <tr style={{ background: '#0f4d2e', color: 'white' }}>
          <th style={{ padding: '8px 6px', textAlign: 'left', fontWeight: 700 }}>{label}</th>
          {range.map(h => <th key={h} style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700 }}>{h}</th>)}
          <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700 }}>{label === 'Front 9' ? 'OUT' : 'IN'}</th>
        </tr>
        <tr style={{ background: '#e8e2d0', color: '#666', fontSize: 12 }}>
          <td style={{ padding: '4px 6px', fontWeight: 600 }}>Par</td>
          {range.map(h => <td key={h} style={{ padding: '4px 6px', textAlign: 'center' }}>{pars[String(h)] ?? pars[h] ?? '—'}</td>)}
          <td style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 700 }}>{sumPar(range) || '—'}</td>
        </tr>
      </thead>
      <tbody>
        {players.map((p, i) => {
          const total = sumRange(p, range);
          return (
            <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '6px', fontWeight: 700, color: '#0f4d2e' }}>{p.name}</td>
              {range.map(h => {
                const s = getScore(p, h);
                const par = Number(pars[String(h)] ?? pars[h]) || null;
                let color = '#333';
                if (par != null && typeof s === 'number') {
                  if (s < par - 1) color = '#7c3aed';
                  else if (s === par - 1) color = '#dc2626';
                  else if (s === par + 1) color = '#2563eb';
                  else if (s > par + 1) color = '#1e3a8a';
                }
                return <td key={h} style={{ padding: '6px', textAlign: 'center', color, fontWeight: par && typeof s === 'number' && (s < par || s > par + 1) ? 700 : 500 }}>{s ?? '—'}</td>;
              })}
              <td style={{ padding: '6px', textAlign: 'center', fontWeight: 700, color: '#0f4d2e' }}>{total || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div ref={ref} style={{ padding: 24, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#0f4d2e' }}>{round.course_name}</div>
          <div style={{ fontSize: 13, color: '#666' }}>{round.date} · {user?.name || ''}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 40, fontWeight: 900, color: '#0f4d2e', lineHeight: 1 }}>{round.score}</div>
          <div style={{ fontSize: 14, color: '#d4a843', fontWeight: 700 }}>{formatVsPar(round.score_vs_par)}</div>
        </div>
      </div>
      {renderSection(front9, 'Front 9')}
      {numHoles > 9 && renderSection(back9, 'Back 9')}
      <div style={{ marginTop: 12, textAlign: 'center', fontSize: 11, color: '#888', letterSpacing: 1 }}>
        THE BUNDLE GOLF CLUB
      </div>
    </div>
  );
});
ShareScorecardCard.displayName = 'ShareScorecardCard';
